import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Play, Pause, RotateCcw, ArrowLeft } from "lucide-react";
import RoboticsShell from "./RoboticsShell";

/**
 * RoboticsDramaPlayer — cinematic storybook player for /robotics/dramas/{slug}.
 *
 * Reads the static manifest published to
 * /robotics/dramas/{slug}/manifest.json and renders:
 *   - 5 cross-fading scene JPGs with Ken Burns motion
 *   - the SFX-mixed narration MP3
 *   - a scrubber + play/pause + play-again on completion
 *
 * Fully self-contained: no /api call, no auth, no shared STEAM state.
 * Uses the existing ftg-kb / ftg-storybook animation classes (they are
 * palette-neutral transforms) inside the .rbx scope for consistency.
 */
function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

export default function RoboticsDramaPlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [state, setState] = useState("loading");
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [activeIdx, setActiveIdx] = useState(0);
  const audioRef = useRef(null);
  const scrubberRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setState("loading"); setManifest(null); setActiveIdx(0);
    setProgress({ current: 0, total: 0 });
    fetch(`/robotics/dramas/${encodeURIComponent(slug)}/manifest.json`, { cache: "no-store" })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((m) => { if (alive) { setManifest(m); setState("ok"); } })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!manifest || !Array.isArray(manifest.scenes)) return;
    const t = progress.current;
    const idx = manifest.scenes.findIndex((s) => t >= s.start_seconds && t < s.end_seconds);
    if (idx === -1) {
      const last = manifest.scenes.length - 1;
      if (t >= manifest.scenes[last].end_seconds) setActiveIdx(last);
      return;
    }
    if (idx !== activeIdx) setActiveIdx(idx);
  }, [progress.current, manifest, activeIdx]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {}); else el.pause();
  };
  const restart = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play().catch(() => {});
  };
  const onScrub = (e) => {
    const el = audioRef.current;
    if (!el || !progress.total || !scrubberRef.current) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const rel = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = rel * progress.total;
  };

  const activeScene = manifest && manifest.scenes ? manifest.scenes[activeIdx] : null;
  const audioEnded = progress.total > 0 && progress.current >= progress.total - 0.25;

  return (
    <RoboticsShell>
      <section className="rbx-drama" data-testid="rbx-drama-page">
        <button type="button"
                className="rbx-btn rbx-btn--ghost rbx-btn--sm"
                onClick={() => nav("/robotics/heritage")}
                data-testid="rbx-drama-back"
                style={{ marginBottom: 16 }}>
          <ArrowLeft size={14} /> Back to The Clarke Legacy
        </button>

        {state === "loading" && (
          <div data-testid="rbx-drama-loading" className="rbx-card"
               style={{ height: 480, opacity: 0.4 }} />
        )}

        {state === "notfound" && (
          <div className="rbx-card" data-testid="rbx-drama-notfound"
               style={{ padding: 24 }}>
            <h1 className="rbx-page__title">Drama not found.</h1>
            <p style={{ color: "var(--rbx-ink-mute)" }}>
              We couldn't find <code>{slug}</code>. Head back to the heritage page.
            </p>
            <Link className="rbx-btn rbx-btn--primary" to="/robotics/heritage"
                  data-testid="rbx-drama-back-btn">
              The Clarke Legacy
            </Link>
          </div>
        )}

        {state === "ok" && manifest && (
          <>
            <div className="rbx-drama__eyebrow" data-testid="rbx-drama-eyebrow">
              THE CLARKE LEGACY &middot; {manifest.total_duration_seconds
                ? formatTime(manifest.total_duration_seconds)
                : "audio drama"}
            </div>
            <h1 className="rbx-drama__title" data-testid="rbx-drama-title">
              {manifest.title || manifest.name}
            </h1>
            {manifest.hook && (
              <p className="rbx-drama__hook" data-testid="rbx-drama-hook">
                {manifest.hook}
              </p>
            )}

            <div className="rbx-drama__stage ftg-storybook__stage"
                 data-testid="rbx-drama-stage">
              {manifest.scenes.map((s, i) => {
                const isActive = i === activeIdx;
                const m = s.motion || null;
                const durS = Math.max(4, s.end_seconds - s.start_seconds);
                const style = isActive && m ? {
                  "--kb-duration": `${durS}s`,
                  "--kb-origin": m.origin || "center center",
                  "--kb-from-scale": String(m.from_scale ?? 1.0),
                  "--kb-to-scale":   String(m.to_scale ?? (m.scale ?? 1.06)),
                  "--kb-from-x":     `${m.from_translate_pct ?? 0}%`,
                  "--kb-to-x":       `${m.to_translate_pct ?? 0}%`,
                  "--kb-from-y":     `${m.from_translate_pct ?? 0}%`,
                  "--kb-to-y":       `${m.to_translate_pct ?? 0}%`,
                } : undefined;
                const motionCls = isActive && m ? `ftg-kb ftg-kb--${m.type}` : "";
                const webpUrl = (s.image_url || "").replace(/\.jpg$/, ".webp");
                return (
                  <picture key={`${s.index}-${isActive ? "on" : "off"}`}>
                    <source srcSet={webpUrl} type="image/webp" />
                    <img src={s.image_url}
                         alt={s.alt}
                         loading={i === 0 ? "eager" : "lazy"}
                         className={`ftg-storybook__img ${isActive ? "is-active" : ""} ${motionCls}`}
                         style={style}
                         data-testid={`rbx-drama-scene-${s.index}`}
                         data-active={isActive ? "true" : "false"} />
                  </picture>
                );
              })}
              <div className="rbx-drama__pip" aria-hidden="true">
                SCENE {activeIdx + 1} · {manifest.scenes.length}
              </div>
              {audioEnded && (
                <button type="button" onClick={restart}
                        className="rbx-drama__replay"
                        data-testid="rbx-drama-replay">
                  <RotateCcw size={14} /> Play again
                </button>
              )}
            </div>

            {activeScene && (
              <p className="rbx-drama__subtitle" key={activeScene.index}
                 data-testid="rbx-drama-subtitle">
                {activeScene.narration_text}
              </p>
            )}

            <div className="rbx-drama__player" data-testid="rbx-drama-player">
              <button type="button" onClick={togglePlay}
                      className="rbx-drama__play"
                      aria-label={playing ? "Pause" : "Play"}
                      data-testid="rbx-drama-play">
                {playing ? <Pause size={22} /> : <Play size={22} />}
              </button>
              <div className="rbx-drama__player-body">
                <div ref={scrubberRef} className="rbx-drama__scrubber"
                     onClick={onScrub} role="slider"
                     aria-valuemin={0}
                     aria-valuemax={progress.total || 0}
                     aria-valuenow={progress.current || 0}
                     tabIndex={0}
                     data-testid="rbx-drama-scrubber">
                  <div className="rbx-drama__scrubber-fill"
                       style={{ width: `${progress.total ? (100 * progress.current / progress.total) : 0}%` }} />
                </div>
                <div className="rbx-drama__times">
                  <span data-testid="rbx-drama-time-current">{formatTime(progress.current)}</span>
                  <span data-testid="rbx-drama-time-total">{formatTime(progress.total || manifest.total_duration_seconds || 0)}</span>
                </div>
              </div>
            </div>

            <audio ref={audioRef}
                   src={manifest.audio_url}
                   preload="metadata"
                   onLoadedMetadata={(e) => setProgress({ current: 0, total: e.target.duration || 0 })}
                   onTimeUpdate={(e) => setProgress((p) => ({ ...p, current: e.target.currentTime }))}
                   onPlay={() => setPlaying(true)}
                   onPause={() => setPlaying(false)}
                   onEnded={() => setPlaying(false)}
                   data-testid="rbx-drama-audio-el" />
          </>
        )}
      </section>
    </RoboticsShell>
  );
}
